  class CustomBundle extends HTMLElement {
    connectedCallback() {
      this.groups = Array.from(this.querySelectorAll("[data-bundle-group]"));
      this.addButton = this.querySelector("[data-bundle-add]");
      this.selectionNoticeElement = this.querySelector("[data-bundle-selection-notice]");      
      this.selectedProductsElement = this.querySelector("[data-bundle-selected-products]");
      this.originalPriceElement = this.querySelector("[data-bundle-original-price]");
      this.discountedPriceElement = this.querySelector("[data-bundle-discounted-price]");
      this.savingElement = this.querySelector("[data-bundle-saving]");
      this.itemCountElement = this.querySelector("[data-bundle-item-count]");
      this.discount = parseFloat(this.dataset.discount || "0");
      this.mobileMediaQuery = window.matchMedia("(max-width: 900px)");
      this.intersectionObserver = null;
      this.dragState = null;
      this.dragAnimationFrame = null;
      this.suppressNextCardClick = false;

      this.addEventListener("change", this.onChange.bind(this));
      this.addEventListener("click", this.onCardClick.bind(this));
      this.addEventListener("keydown", this.onCardKeyDown.bind(this));
      this.addEventListener("click", this.onGroupTitleClick.bind(this));
      this.addEventListener("mousedown", this.onProductGridMouseDown.bind(this));
      window.addEventListener("mousemove", this.onProductGridMouseMove.bind(this));
      window.addEventListener("mouseup", this.onProductGridMouseUp.bind(this));
      this.addEventListener("toggle", this.onGroupToggle.bind(this), true);
      this.addButton?.addEventListener("click", this.onAddToCart.bind(this));
      this.mobileMediaQuery.addEventListener("change", this.onMobileMediaChanged.bind(this));
      this.onMobileMediaChanged();
      this.update();
    }

    onMobileMediaChanged() {
      this.setupMobileAccordions();
      this.setupMobileSummaryVisibility();
      this.update();
    }

    setupMobileAccordions() {
      this.groups.forEach((group, index) => {
        group.open = !this.mobileMediaQuery.matches || index === 0;
      });
    }

    setupMobileSummaryVisibility() {
      this.setMobileSummaryVisibility(!this.mobileMediaQuery.matches);

      if (this.intersectionObserver) {
        this.intersectionObserver.disconnect();
        this.intersectionObserver = null;
      }

      if (!this.mobileMediaQuery.matches) {
        return;
      }

      this.intersectionObserver = new IntersectionObserver((entries) => {
        this.setMobileSummaryVisibility(entries.some((entry) => entry.isIntersecting));
      }, {
        threshold: 0.08
      });

      this.intersectionObserver.observe(this);
    }

    setMobileSummaryVisibility(isVisible) {
      this.classList.toggle("is-mobile-summary-visible", isVisible);
      document.body.classList.toggle("is-custom-bundle-summary-visible", isVisible && this.mobileMediaQuery.matches);
    }

    onProductGridMouseDown(event) {
      if (this.mobileMediaQuery.matches || event.button !== 0) {
        return;
      }

      const productGrid = event.target.closest(".custom-bundle__product-grid");

      if (!productGrid || productGrid.scrollWidth <= productGrid.clientWidth) {
        return;
      }

      this.dragState = {
        productGrid,
        startX: event.clientX,
        scrollLeft: productGrid.scrollLeft,
        targetScrollLeft: productGrid.scrollLeft,
        hasMoved: false
      };

      productGrid.classList.add("is-dragging");
      event.preventDefault();
    }

    onProductGridMouseMove(event) {
      if (!this.dragState) {
        return;
      }

      const distance = event.clientX - this.dragState.startX;

      if (Math.abs(distance) > 3) {
        this.dragState.hasMoved = true;
      }

      this.dragState.targetScrollLeft = this.dragState.scrollLeft - distance;
      this.requestProductGridDragFrame();
      event.preventDefault();
    }

    requestProductGridDragFrame() {
      if (this.dragAnimationFrame) {
        return;
      }

      this.dragAnimationFrame = requestAnimationFrame(() => {
        this.dragAnimationFrame = null;

        if (!this.dragState) {
          return;
        }

        this.dragState.productGrid.scrollLeft = this.dragState.targetScrollLeft;
      });
    }

    onProductGridMouseUp() {
      if (!this.dragState) {
        return;
      }

      const productGrid = this.dragState.productGrid;
      const targetScrollLeft = this.dragState.targetScrollLeft;

      productGrid.style.scrollBehavior = "smooth";
      productGrid.classList.remove("is-dragging");
      productGrid.scrollTo({ left: targetScrollLeft, behavior: "smooth" });

      if (this.dragState.hasMoved) {
        this.suppressNextCardClick = true;

        setTimeout(() => {
          this.suppressNextCardClick = false;
        });
      }

      this.dragState = null;

      if (this.dragAnimationFrame) {
        cancelAnimationFrame(this.dragAnimationFrame);
        this.dragAnimationFrame = null;
      }

      setTimeout(() => {
        productGrid.style.scrollBehavior = "";
      }, 250);
    }

    onCardClick(event) {
      const label = event.target.closest(".custom-bundle-card__label");

      if (!label) {
        return;
      }

      if (this.suppressNextCardClick) {
        event.preventDefault();
        return;
      }

      const input = document.getElementById(label.getAttribute("for"));

      if (!input || input.disabled) {
        return;
      }

      event.preventDefault();

      const productGrid = label.closest(".custom-bundle__product-grid");
      const pageScrollX = window.scrollX;
      const pageScrollY = window.scrollY;
      const gridScrollLeft = productGrid?.scrollLeft;

      if (input.type === "radio") {
        input.checked = true;
      } else {
        input.checked = !input.checked;
      }

      input.dispatchEvent(new Event("change", { bubbles: true }));

      requestAnimationFrame(() => {
        if (typeof gridScrollLeft === "number") {
          productGrid.scrollLeft = gridScrollLeft;
        }

        window.scrollTo(pageScrollX, pageScrollY);
      });
    }

    onCardKeyDown(event) {
      if (event.key !== " " && event.key !== "Enter") {
        return;
      }

      const label = event.target.closest(".custom-bundle-card__label");

      if (!label) {
        return;
      }

      event.preventDefault();
      label.click();
    }

    onGroupTitleClick(event) {
      const groupTitle = event.target.closest(".custom-bundle__group-title");

      if (!groupTitle || this.mobileMediaQuery.matches) {
        return;
      }

      event.preventDefault();
      groupTitle.closest("[data-bundle-group]").open = true;
    }

    onGroupToggle(event) {
      const group = event.target.closest("[data-bundle-group]");

      if (!group) {
        return;
      }

      if (!this.mobileMediaQuery.matches) {
        if (!group.open) {
          group.open = true;
        }

        return;
      }

      if (!group.open) {
        return;
      }

      this.groups.forEach((otherGroup) => {
        if (otherGroup !== group) {
          otherGroup.open = false;
        }
      });
    }

    onChange(event) {
      const input = event.target.closest("[data-bundle-input]");

      if (!input) {
        return;
      }

      const group = input.closest("[data-bundle-group]");
      const selectionMode = this.getSelectionMode(group);

      if (selectionMode === "one" && group?.dataset.required === "true" && input.type === "checkbox" && !input.checked) {
        const hasOtherSelection = Array.from(group.querySelectorAll("[data-bundle-input]")).some((groupInput) => {
          return groupInput !== input && groupInput.checked && !groupInput.disabled;
        });

        if (!hasOtherSelection) {
          input.checked = true;
        }
      }

      if (selectionMode === "one" && input.type === "checkbox" && input.checked) {
        group.querySelectorAll("[data-bundle-input]").forEach((groupInput) => {
          if (groupInput !== input) {
            groupInput.checked = false;
          }
        });
      }

      this.update();
    }

    getSelectionMode(group) {
      if (!group) {
        return "";
      }

      const explicitMode = group.dataset.selectionMode || "";
      const groupType = (group.dataset.groupType || "").toLowerCase().replace(/\s*-\s*/g, " ");

      if (groupType.includes("select quantity")) {
        return "quantity";
      }

      if (groupType.includes("select one")) {
        return "one";
      }

      if (explicitMode) {
        return explicitMode;
      }

      return "multiple";
    }

    getSelectedCards() {
      return this.groups.flatMap((group) => {
        return Array.from(group.querySelectorAll("[data-bundle-card]")).filter((card) => {
          const input = card.querySelector("[data-bundle-input]");
          return input?.checked && !input.disabled;
        });
      });
    }

    getSelectedItems() {
      return this.getSelectedCards().map((card) => {
        const quantity = parseInt(card.dataset.quantity || "1", 10);
        const price = parseInt(card.dataset.price || "0", 10);
        const freeGiftId = card.dataset.freeGiftId;
        const freeGiftVariantId = card.dataset.freeGiftVariantId;

        return {
          id: card.dataset.variantId,
          productId: card.dataset.productId,
          title: card.dataset.title,
          price,
          quantity,
          image: card.dataset.image,
          linePrice: price * quantity,
          freeGiftId,
          freeGiftVariantId
        };
      });
    }

    update() {
      const selectedItems = this.getSelectedItems();
      const itemCount = selectedItems.reduce((total, item) => total + item.quantity, 0);      
      const originalTotal = selectedItems.reduce((total, item) => total + item.linePrice, 0);
      const saving = Math.floor(originalTotal * this.discount / 100);
      const discountedTotal = originalTotal - saving;
      const isAddable = this.isValid() && selectedItems.length > 1;      

      this.updateCards();
      this.renderSelectedProducts(selectedItems);

      if (this.originalPriceElement) {
        this.originalPriceElement.textContent = this.formatMoney(originalTotal);
      }

      if (this.discountedPriceElement) {
        this.discountedPriceElement.textContent = this.formatMoney(discountedTotal);
      }

      if (this.savingElement) {
        const savingsTemplate = this.mobileMediaQuery.matches ? this.dataset.mobileSavingsTemplate : this.dataset.savingsTemplate;

        this.savingElement.innerHTML = savingsTemplate
          .replace("[amount]", this.formatMoney(saving))
          .replace("[discount]", this.discount.toString());
      }

      if (this.itemCountElement) {
        const template = itemCount === 1 ? this.dataset.itemCountOne : this.dataset.itemCountOther;
        this.itemCountElement.textContent = template.replace("[count]", itemCount.toString());
      }

      if (this.addButton) {
        if (this.addButton.getAttribute("aria-busy") !== "true") {
          this.addButton.disabled = false;
        }

        this.addButton.setAttribute("aria-disabled", isAddable ? "false" : "true");
      }

      if (this.selectionNoticeElement) {
        this.selectionNoticeElement.hidden = selectedItems.length !== 1;
      }

    }

    updateCards() {
      this.querySelectorAll("[data-bundle-card]").forEach((card) => {
        const input = card.querySelector("[data-bundle-input]");
        const label = card.querySelector(".custom-bundle-card__label");
        const button = card.querySelector("[data-bundle-card-button]");

        if (!button || !input) {
          return;
        }

        if (input.disabled) {
          button.textContent = this.dataset.outOfStockText;
          label?.setAttribute("aria-checked", "false");
          label?.setAttribute("aria-disabled", "true");
          return;
        }

        button.textContent = input.checked ? this.dataset.inBundleText : this.dataset.selectProductText;
        label?.setAttribute("aria-checked", input.checked ? "true" : "false");
        label?.setAttribute("aria-disabled", "false");
      });
    }

    renderSelectedProducts(items) {
      if (!this.selectedProductsElement) {
        return;
      }

      this.selectedProductsElement.innerHTML = items.map((item) => {
        const quantityBadge = item.quantity > 1 ? `<span class="custom-bundle__selected-product-quantity">x${item.quantity}</span>` : "";

        return `
          <div class="custom-bundle__selected-product">
            ${item.image ? `<img class="custom-bundle__selected-product-image" src="${item.image}" alt="" loading="lazy">` : `<span class="custom-bundle__selected-product-image"></span>`}
            <div>
              <p class="custom-bundle__selected-product-title">${this.escapeHtml(item.title)}${quantityBadge}</p>
              <span class="custom-bundle__selected-product-price">${this.formatMoney(item.linePrice)}</span>
            </div>
          </div>
        `;
      }).join("");
    }

    isValid() {
      return this.groups.every((group) => {
        if (group.dataset.required !== "true") {
          return true;
        }

        return Array.from(group.querySelectorAll("[data-bundle-input]")).some((input) => input.checked && !input.disabled);
      });
    }

    async onAddToCart() {
      const selectedItems = this.getSelectedItems();

      if (!this.isValid() || selectedItems.length <= 1) {
        this.scrollToSelectionNotice();

        console.warn("Custom bundle add blocked: required selections are incomplete.", this.getValidationState());
        return;
      }

      if (selectedItems.length === 0) {
        console.warn("Custom bundle add blocked: no selected items found.");
        return;
      }

      this.addButton?.setAttribute("disabled", "disabled");
      this.addButton?.setAttribute("aria-busy", "true");

      const properties = {
        "_bundle_id": `_bundle_${Date.now()}`,
        "_bundle_label": this.dataset.bundleTitle,
        "_bundle_custom_img": this.getBundleCustomImage(),
        "_bundle_discount_percent": this.discount.toString(),
        "_bundle_parent_id": this.getBundleParentGid(),
        "_bundle_source_url": this.dataset.mainProductUrl
      };

      console.info("Custom bundle add started.", selectedItems);
      console.info("Custom bundle line item properties.", properties);

      const formData = new FormData();

      selectedItems.forEach((item, index) => {
        formData.append(`items[${index}][id]`, item.id);
        formData.append(`items[${index}][quantity]`, item.quantity);
        if (item.freeGiftId) {
          formData.append(`items[${index}][properties][_free_gift_id]`, item.freeGiftId);
        }

        Object.entries(properties).forEach(([key, value]) => {
          formData.append(`items[${index}][properties][${key}]`, value);
        });
      });

      const freeGiftItems = selectedItems.filter((item) => item.freeGiftId);
      const itemsLength = selectedItems.length;
      const productIdsInCart = (await (await fetch(`${window.themeVariables.routes.cartUrl}.js`)).json()).items.map((item) => item.product_id);

      freeGiftItems.forEach((item, index) => {
        if (!item.freeGiftId || productIdsInCart.includes(Number(item.freeGiftId))) {
          return;
        }

        formData.append(`items[${itemsLength + index}][id]`, item.freeGiftVariantId);
        formData.append(`items[${itemsLength + index}][quantity]`, 1);
      });

      const response = await fetch(`${window.themeVariables.routes.cartAddUrl}.js`, {
        body: formData,
        credentials: "same-origin",
        method: "POST",
        headers: {
          "X-Requested-With": "XMLHttpRequest"
        }
      });

      const responseJson = await this.parseResponseJson(response);
      console.info("Custom bundle add response.", response.status, responseJson);

      if (response.status === 401 || response.status === 403) {
        this.submitBundleForm(selectedItems, properties);
        return;
      }

      this.addButton?.removeAttribute("disabled");
      this.addButton?.removeAttribute("aria-busy");

      if (response.ok) {
        if (window.themeVariables.settings.cartType === "page" || window.themeVariables.settings.pageType === "cart") {
          window.location.href = `${Shopify.routes.root}cart`;
          return;
        }

        const cartResponse = await fetch(`${window.themeVariables.routes.cartUrl}.js`);
        const cartContent = await cartResponse.json();

        document.documentElement.dispatchEvent(new CustomEvent("cart:updated", {
          bubbles: true,
          detail: {
            cart: cartContent
          }
        }));

        document.documentElement.dispatchEvent(new CustomEvent("cart:refresh", {
          bubbles: true,
          detail: {
            cart: cartContent,
            openMiniCart: window.themeVariables.settings.cartType === "drawer"
          }
        }));
      }

      this.dispatchEvent(new CustomEvent("cart-notification:show", {
        bubbles: true,
        cancelable: true,
        detail: {
          status: response.ok ? "success" : "error",
          error: responseJson?.description || ""
        }
      }));
    }

    scrollToSelectionNotice() {
      if (!this.selectionNoticeElement || this.selectionNoticeElement.hidden) {
        return;
      }

      this.selectionNoticeElement.scrollIntoView({ behavior: "smooth", block: "center" });
    }

    submitBundleForm(items, properties) {
      console.info("Custom bundle add falling back to form submit.", items);
      console.info("Custom bundle fallback line item properties.", properties);
      const form = document.createElement("form");
      form.method = "post";
      form.action = window.themeVariables.routes.cartAddUrl;
      form.hidden = true;

      items.forEach((item, index) => {
        this.appendFormInput(form, `items[${index}][id]`, item.id);
        this.appendFormInput(form, `items[${index}][quantity]`, item.quantity);

        Object.entries(properties).forEach(([key, value]) => {
          this.appendFormInput(form, `items[${index}][properties][${key}]`, value);
        });
      });

      document.body.appendChild(form);
      form.submit();
    }

    getBundleCustomImage() {
      const groupOne = this.querySelector('[data-bundle-group][data-group-id="1"]');
      const selectedCard = groupOne?.querySelector("[data-bundle-input]:checked")?.closest("[data-bundle-card]");
      return this.normalizeHttpsUrl(selectedCard?.dataset.image || "");
    }

    getBundleParentGid() {
      const parentId = this.dataset.bundleParentId || "";

      if (!parentId) {
        return "";
      }

      if (parentId.startsWith("gid://")) {
        return parentId;
      }

      return `gid://shopify/ProductVariant/${parentId}`;
    }

    normalizeHttpsUrl(url) {
      if (!url) {
        return "";
      }

      if (url.startsWith("//")) {
        return `https:${url}`;
      }

      if (url.startsWith("http://")) {
        return url.replace("http://", "https://");
      }

      if (url.startsWith("https://")) {
        return url;
      }

      return new URL(url, window.location.origin).href.replace("http://", "https://");
    }

    getValidationState() {
      return this.groups.map((group) => {
        return {
          groupId: group.dataset.groupId,
          groupType: group.dataset.groupType,
          selectionMode: this.getSelectionMode(group),
          required: group.dataset.required,
          selectedCount: Array.from(group.querySelectorAll("[data-bundle-input]")).filter((input) => input.checked && !input.disabled).length
        };
      });
    }

    appendFormInput(form, name, value) {
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = name;
      input.value = value;
      form.appendChild(input);
    }

    async parseResponseJson(response) {
      const text = await response.text();

      if (!text) {
        return {};
      }

      try {
        return JSON.parse(text);
      } catch (error) {
        return {};
      }
    }

    formatMoney(cents) {
      if (typeof formatMoney === "function") {
        const format = window.themeVariables.settings.currencyCodeEnabled ? window.themeVariables.settings.moneyWithCurrencyFormat : window.themeVariables.settings.moneyFormat;
        return formatMoney(cents, format);
      }

      return (cents / 100).toFixed(2);
    }

    escapeHtml(value) {
      const div = document.createElement("div");
      div.textContent = value;
      return div.innerHTML;
    }
  }

  if (!window.customElements.get("custom-bundle")) {
    window.customElements.define("custom-bundle", CustomBundle);
  }

  if (!window.themeVariables.customBundleScrollButtonLoaded) {
    window.themeVariables.customBundleScrollButtonLoaded = true;

    document.addEventListener("click", (event) => {
      const button = event.target.closest("[data-custom-bundle-scroll]");

      if (!button) {
        return;
      }

      const customBundle = document.querySelector(".custom-bundle");

      if (customBundle) {
        customBundle.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    });
  }
